import React, { useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FaUpload, FaCode, FaDatabase, FaFileAlt, FaCopy, FaTools, FaGlobe, FaChartBar as FaChartBarIcon, FaBrain as FaBrainIcon, FaTrash, FaPlay, FaDownload, FaTachometerAlt } from 'react-icons/fa';
import { AiFillHome } from 'react-icons/ai';
import { Link } from '@remix-run/react';
import type { MetaFunction } from '@remix-run/node';
import { useTranslation } from '~/shared/utils/i18n';
import { getServiceKeywords, createSEODescription } from "~/shared/utils/seo-keywords";
import { analyzeDataOptimized, generateCodeOptimized, type AnalysisResult, type DataField } from '~/shared/utils/data-analyzer-optimized';
import { analyzeDataForVisualization } from '~/shared/utils/data-visualizer';
import AdvancedDashboard from '~/shared/components/AdvancedDashboard';
import LoadingProgress from '~/shared/components/LoadingProgress';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
} from 'chart.js';
import { Line, Bar, Doughnut, Scatter } from 'react-chartjs-2';
import '~/features/data-analytics/data-analytics.css';
import '~/features/data-analytics/advanced-dashboard.css';

// تسجيل مكونات Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale
);

// ============================================================================
// SEO METADATA - تحسين محركات البحث
// ============================================================================

export const meta: MetaFunction = () => {
  const keywords = getServiceKeywords('dataAnalytics');
  const keywordsString = keywords.join(', ');
  const description = createSEODescription('dataAnalytics');
  
  return [
    // العنوان الرئيسي المحسن لـ SEO
    { title: "محلل البيانات الذكي المتقدم - أسامة الزبيدي | تحليل البيانات وتوليد الأكواد | Data Analytics - Osama Al-Zubaidi" },
    
    // الوصف المحسن لـ SEO
    { name: "description", content: description },
    
    // الكلمات المفتاحية المحسنة
    { name: "keywords", content: keywordsString },
    
    // معلومات المؤلف
    { name: "author", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { name: "creator", content: "أسامة الزبيدي" },
    { name: "publisher", content: "أسامة الزبيدي" },
    { name: "copyright", content: "صنع بكل ❤️ في 🇸🇦" },
    
    // إعدادات الروبوتات
    { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "googlebot", content: "index, follow" },
    { name: "bingbot", content: "index, follow" },
    
    // معلومات اللغة والموقع
    { name: "language", content: "Arabic, English" },
    { name: "geo.region", content: "SA" },
    { name: "geo.country", content: "Saudi Arabia" },
    
    // Open Graph - محسن لـ SEO
    { property: "og:title", content: "محلل البيانات الذكي المتقدم - أسامة الزبيدي | تحليل البيانات وتوليد الأكواد" },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://oz.sa/data-analytics" },
    { property: "og:site_name", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { property: "og:locale", content: "ar_AR" },
    { property: "og:locale:alternate", content: "en_US" },
    { property: "og:image", content: "https://oz.sa/logo-light.png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: "محلل البيانات الذكي المتقدم - أسامة الزبيدي" },
    { property: "og:image:type", content: "image/png" },
    { property: "og:image:secure_url", content: "https://oz.sa/logo-light.png" },
    
    // Twitter Cards - محسن لـ SEO
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "محلل البيانات الذكي المتقدم - أسامة الزبيدي | تحليل البيانات وتوليد الأكواد" },
    { name: "twitter:description", content: description },
    { name: "twitter:creator", content: "@osama_zubaidi" },
    { name: "twitter:site", content: "@osama_zubaidi" },
    { name: "twitter:image", content: "https://oz.sa/logo-light.png" },
    { name: "twitter:image:alt", content: "محلل البيانات الذكي المتقدم - أسامة الزبيدي" },
    { name: "twitter:domain", content: "oz.sa" },
    
    // معلومات إضافية لـ SEO
    { name: "canonical", content: "https://oz.sa/data-analytics" },
    { name: "alternate", content: "https://oz.sa/data-analytics", "hreflang": "ar" },
    { name: "alternate", content: "https://oz.sa/data-analytics", "hreflang": "en" },
    { name: "alternate", content: "https://oz.sa/data-analytics", "hreflang": "x-default" },
    
    // معلومات إضافية للتحسين
    { name: "rating", content: "General" },
    { name: "distribution", content: "Global" },
    { name: "coverage", content: "Worldwide" },
    { name: "target", content: "all" },
  ];
};

// =====================
// أنواع البيانات
// =====================



interface AnalysisState {
  isAnalyzing: boolean;
  result: AnalysisResult | null;
  error: string | null;
}

// =====================
// البيانات والتكوين
// =====================
const dataAnalyticsTranslations = {
  ar: {
    title: "محلل البيانات الذكي المتقدم",
    subtitle: "حلل البيانات وحول JSON/CSV إلى أكواد مُكتوبة تلقائياً مع كشف ذكي للأنواع",
    heroTitle: "محلل البيانات المتقدم",
    heroSubtitle: "حول أي بيانات JSON أو CSV إلى أكواد مُكتوبة تلقائياً مع تحليل ذكي شامل للأنواع والهياكل والعلاقات",
    uploadTitle: "رفع البيانات",
    uploadSubtitle: "اسحب وأفلت ملف JSON أو CSV أو اكتب البيانات مباشرة",
    analysisTitle: "تحليل البيانات المتقدم",
    analysisSubtitle: "اكتشف الأنواع والهياكل والعلاقات والإحصائيات في بياناتك",
    outputTitle: "الأكواد المُولدة",
    outputSubtitle: "احصل على أكواد مُكتوبة تلقائياً لعدة لغات وبيئات",
    features: {
      title: "المميزات المتقدمة",
      items: [
        {
          icon: "brain",
          title: "كشف ذكي للأنواع",
          description: "اكتشف تلقائياً المعرفات والتواريخ والعملات والإحداثيات وأرقام الهواتف"
        },
        {
          icon: "code",
          title: "أكواد متعددة اللغات",
          description: "احصل على أكواد TypeScript و SQL و Go و Zod و Mongoose وغيرها"
        },
        {
          icon: "chart",
          title: "تحليل إحصائي شامل",
          description: "احصل على إحصائيات مفصلة والنسب المئوية والأنماط"
        },
        {
          icon: "database",
          title: "هندسة قواعد البيانات",
          description: "اكتشف العلاقات واقترح المفاتيح والفهارس والقيود"
        }
      ]
    },
    supportedFormats: {
      title: "الصيغ المدعومة",
      input: ["JSON", "CSV", "XML", "YAML"],
      output: ["TypeScript", "SQL", "Go", "Zod", "Mongoose", "Knex", "JSON Schema", "Protocol Buffers"]
    },
    examples: {
      title: "أمثلة سريعة",
      items: [
        {
          name: "API Response",
          description: "تحليل استجابة API وتحويلها إلى TypeScript",
          data: JSON.stringify([
            { 
              id: 1, 
              name: "John Doe", 
              email: "john@example.com", 
              age: 25, 
              active: true,
              created_at: "2024-01-15T10:30:00Z",
              salary: 50000.50,
              phone: "+966501234567",
              location: { lat: 24.7136, lng: 46.6753 }
            },
            { 
              id: 2, 
              name: "Jane Smith", 
              email: "jane@example.com", 
              age: 30, 
              active: false,
              created_at: "2024-01-16T14:45:00Z",
              salary: 65000.75,
              phone: "+966507654321",
              location: { lat: 21.4858, lng: 39.1925 }
            }
          ], null, 2)
        },
        {
          name: "E-commerce Data",
          description: "تحليل بيانات التجارة الإلكترونية مع المنتجات والطلبات",
          data: JSON.stringify([
            { 
              product_id: "P001", 
              name: "Laptop Pro", 
              price: 999.99, 
              category: "Electronics", 
              in_stock: true,
              tags: ["gaming", "professional"],
              ratings: [4.5, 4.8, 4.2],
              metadata: {
                brand: "TechCorp",
                warranty: "2 years",
                weight: 2.5
              }
            },
            { 
              product_id: "P002", 
              name: "Wireless Mouse", 
              price: 29.99, 
              category: "Accessories", 
              in_stock: false,
              tags: ["wireless", "ergonomic"],
              ratings: [4.0, 3.8, 4.1],
              metadata: {
                brand: "MouseTech",
                warranty: "1 year",
                weight: 0.1
              }
            }
          ], null, 2)
        },
        {
          name: "CSV Sales Data",
          description: "تحليل بيانات المبيعات بصيغة CSV",
          data: `name,age,salary,department,hire_date
أحمد محمد,25,50000,تطوير,2023-01-15
فاطمة علي,30,65000,تصميم,2022-06-20
محمد أحمد,28,55000,تسويق,2023-03-10
سارة خالد,32,70000,إدارة,2021-12-05
علي حسن,27,48000,تطوير,2023-08-12
نورا سعد,29,60000,تصميم,2022-11-30
خالد عمر,35,80000,إدارة,2020-09-15
ليلى أحمد,26,52000,تسويق,2023-05-22`
        }
      ]
    },
    actions: {
      analyze: "تحليل البيانات",
      analyzing: "جاري التحليل...",
      clear: "مسح البيانات",
      copy: "نسخ الكود",
      copied: "تم النسخ!",
      download: "تحميل الكود",
      view: "عرض التفاصيل",
      edit: "تعديل المخطط"
    },
    analysis: {
      statistics: "الإحصائيات",
      fields: "الحقول المكتشفة",
      relationships: "العلاقات",
      suggestions: "الاقتراحات",
      totalRecords: "إجمالي السجلات",
      totalFields: "إجمالي الحقول",
      uniqueFields: "الحقول الفريدة",
      enumFields: "حقول التعداد",
      idFields: "حقول المعرفات",
      requiredFields: "الحقول المطلوبة",
      nullableFields: "الحقول القابلة للفراغ"
    }
  },
  en: {
    title: "Advanced Smart Data Analyzer",
    subtitle: "Analyze data and convert JSON/CSV to auto-generated code with intelligent type detection",
    heroTitle: "Advanced Data Analyzer",
    heroSubtitle: "Convert any JSON or CSV data to auto-generated code with comprehensive intelligent analysis of types, structures, and relationships",
    uploadTitle: "Upload Data",
    uploadSubtitle: "Drag & drop JSON or CSV files or type data directly",
    analysisTitle: "Advanced Data Analysis",
    analysisSubtitle: "Discover types, structures, relationships, and statistics in your data",
    outputTitle: "Generated Code",
    outputSubtitle: "Get auto-generated code for multiple languages and environments",
    features: {
      title: "Advanced Features",
      items: [
        {
          icon: "brain",
          title: "Intelligent Type Detection",
          description: "Automatically discover IDs, dates, currencies, coordinates, and phone numbers"
        },
        {
          icon: "code",
          title: "Multi-Language Code",
          description: "Get TypeScript, SQL, Go, Zod, Mongoose, and other code"
        },
        {
          icon: "chart",
          title: "Comprehensive Statistics",
          description: "Get detailed statistics, percentiles, and patterns"
        },
        {
          icon: "database",
          title: "Database Engineering",
          description: "Discover relationships and suggest keys, indexes, and constraints"
        }
      ]
    },
    supportedFormats: {
      title: "Supported Formats",
      input: ["JSON", "CSV", "XML", "YAML"],
      output: ["TypeScript", "SQL", "Go", "Zod", "Mongoose", "Knex", "JSON Schema", "Protocol Buffers"]
    },
    examples: {
      title: "Quick Examples",
      items: [
        {
          name: "API Response",
          description: "Analyze API response and convert to TypeScript",
          data: JSON.stringify([
            { 
              id: 1, 
              name: "John Doe", 
              email: "john@example.com", 
              age: 25, 
              active: true,
              created_at: "2024-01-15T10:30:00Z",
              salary: 50000.50,
              phone: "+966501234567",
              location: { lat: 24.7136, lng: 46.6753 }
            },
            { 
              id: 2, 
              name: "Jane Smith", 
              email: "jane@example.com", 
              age: 30, 
              active: false,
              created_at: "2024-01-16T14:45:00Z",
              salary: 65000.75,
              phone: "+966507654321",
              location: { lat: 21.4858, lng: 39.1925 }
            }
          ], null, 2)
        },
        {
          name: "E-commerce Data",
          description: "Analyze e-commerce data with products and orders",
          data: JSON.stringify([
            { 
              product_id: "P001", 
              name: "Laptop Pro", 
              price: 999.99, 
              category: "Electronics", 
              in_stock: true,
              tags: ["gaming", "professional"],
              ratings: [4.5, 4.8, 4.2],
              metadata: {
                brand: "TechCorp",
                warranty: "2 years",
                weight: 2.5
              }
            },
            { 
              product_id: "P002", 
              name: "Wireless Mouse", 
              price: 29.99, 
              category: "Accessories", 
              in_stock: false,
              tags: ["wireless", "ergonomic"],
              ratings: [4.0, 3.8, 4.1],
              metadata: {
                brand: "MouseTech",
                warranty: "1 year",
                weight: 0.1
              }
            }
          ], null, 2)
        },
        {
          name: "CSV Sales Data",
          description: "Analyze sales data in CSV format",
          data: `name,age,salary,department,hire_date
Ahmed Mohamed,25,50000,Development,2023-01-15
Fatima Ali,30,65000,Design,2022-06-20
Mohamed Ahmed,28,55000,Marketing,2023-03-10
Sarah Khalid,32,70000,Management,2021-12-05
Ali Hassan,27,48000,Development,2023-08-12
Nora Saad,29,60000,Design,2022-11-30
Khalid Omar,35,80000,Management,2020-09-15
Layla Ahmed,26,52000,Marketing,2023-05-22`
        }
      ]
    },
    actions: {
      analyze: "Analyze Data",
      analyzing: "Analyzing...",
      clear: "Clear Data",
      copy: "Copy Code",
      copied: "Copied!",
      download: "Download Code",
      view: "View Details",
      edit: "Edit Schema"
    },
    analysis: {
      statistics: "Statistics",
      fields: "Discovered Fields",
      relationships: "Relationships",
      suggestions: "Suggestions",
      totalRecords: "Total Records",
      totalFields: "Total Fields",
      uniqueFields: "Unique Fields",
      enumFields: "Enum Fields",
      idFields: "ID Fields",
      requiredFields: "Required Fields",
      nullableFields: "Nullable Fields"
    }
  }
};

// =====================
// مكونات الأيقونات
// =====================
function FeatureIcon({ icon, className = '' }: { icon: string; className?: string }) {
  const iconClasses = `w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 ${className}`;
  
  switch (icon) {
    case 'brain':
      return <FaBrainIcon className={iconClasses} />;
    case 'code':
      return <FaCode className={iconClasses} />;
    case 'chart':
      return <FaChartBarIcon className={iconClasses} />;
    case 'database':
      return <FaDatabase className={iconClasses} />;
    default:
      return <FaTools className={iconClasses} />;
  }
}

// =====================
// مكونات الصفحة
// =====================
function DataUpload({ onDataChange, currentLanguage, onClear }: { 
  onDataChange: (data: string) => void; 
  currentLanguage: 'ar' | 'en';
  onClear: () => void;
}) {
  const [dragActive, setDragActive] = useState(false);
  const [inputData, setInputData] = useState('');
  const t = dataAnalyticsTranslations[currentLanguage];

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputData(content);
        onDataChange(content);
      };
      reader.readAsText(file);
    }
  }, [onDataChange]);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputData(content);
        onDataChange(content);
      };
      reader.readAsText(file);
    }
  }, [onDataChange]);

  const handleDataChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const data = e.target.value;
    setInputData(data);
    onDataChange(data);
  }, [onDataChange]);

  const handleClear = useCallback(() => {
    setInputData('');
    onDataChange('');
    onClear();
  }, [onDataChange, onClear]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="w-full max-w-4xl mx-auto"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          {t.uploadTitle}
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          {t.uploadSubtitle}
        </p>
      </div>

      <div
        className={`relative border-2 border-dashed rounded-2xl p-8 transition-all duration-300 ${
          dragActive
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="text-center">
          <FaUpload className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
          <p className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
            {currentLanguage === 'ar' ? 'اسحب وأفلت الملف هنا' : 'Drag and drop file here'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {currentLanguage === 'ar' ? 'أو' : 'or'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <label className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl cursor-pointer hover:bg-blue-700 transition-colors">
              <FaFileAlt className="w-5 h-5 mr-2" />
              {currentLanguage === 'ar' ? 'اختر ملف' : 'Choose File'}
              <input
                type="file"
                accept=".json,.csv,.txt"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
            {inputData && (
              <button
                onClick={handleClear}
                className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
              >
                <FaTrash className="w-5 h-5 mr-2" />
                {t.actions.clear}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8">
        <label className="block text-lg font-medium text-gray-700 dark:text-gray-300 mb-4">
          {currentLanguage === 'ar' ? 'أو اكتب البيانات مباشرة:' : 'Or type data directly:'}
        </label>
        <textarea
          value={inputData}
          onChange={handleDataChange}
          placeholder={currentLanguage === 'ar' ? 'ضع بيانات JSON أو CSV هنا...' : 'Paste JSON or CSV data here...'}
          className="w-full h-64 p-4 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
      </div>
    </motion.div>
  );
}

function DataAnalysis({ data, currentLanguage, onAnalysisComplete }: { 
  data: string; 
  currentLanguage: 'ar' | 'en';
  onAnalysisComplete: (result: AnalysisResult) => void;
}) {
  const [analysis, setAnalysis] = useState<AnalysisState>({
    isAnalyzing: false,
    result: null,
    error: null
  });
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState('');
  const [showProgress, setShowProgress] = useState(false);
  
  const t = dataAnalyticsTranslations[currentLanguage];

  const analyzeData = useCallback(async () => {
    if (!data.trim()) return;

    setAnalysis(prev => ({ ...prev, isAnalyzing: true, error: null }));
    setShowProgress(true);
    setProgress(0);
    setStage(t.actions.analyzing);
    
    try {
      const result = await analyzeDataOptimized(data, (progress, stage) => {
        setProgress(progress);
        setStage(stage);
      });
      
      setAnalysis({
        isAnalyzing: false,
        result,
        error: null
      });
      
      onAnalysisComplete(result);
    } catch (error) {
      console.error('Error analyzing data:', error);
      setAnalysis({
        isAnalyzing: false,
        result: null,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }, [data, onAnalysisComplete, t.actions.analyzing]);

  return (
    <>
      <LoadingProgress
        progress={progress}
        stage={stage}
        isVisible={showProgress}
        onComplete={() => setShowProgress(false)}
        currentLanguage={currentLanguage}
      />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="w-full max-w-6xl mx-auto"
      >
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          {t.analysisTitle}
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          {t.analysisSubtitle}
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            {currentLanguage === 'ar' ? 'تحليل البيانات المتقدم' : 'Advanced Data Analysis'}
          </h3>
          <button
            onClick={analyzeData}
            disabled={!data.trim() || analysis.isAnalyzing}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {analysis.isAnalyzing ? (
              <>
                <FaPlay className="w-4 h-4 mr-2 animate-spin" />
                {t.actions.analyzing}
              </>
            ) : (
              <>
                <FaPlay className="w-4 h-4 mr-2" />
                {t.actions.analyze}
              </>
            )}
          </button>
        </div>

        {analysis.error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <p className="text-red-800 dark:text-red-200">{analysis.error}</p>
          </div>
        )}

        {analysis.result && (
          <AnalysisResults result={analysis.result} currentLanguage={currentLanguage} />
        )}
      </div>
    </motion.div>
    </>
  );
}

function AnalysisResults({ result, currentLanguage }: { result: AnalysisResult; currentLanguage: 'ar' | 'en' }) {
  const t = dataAnalyticsTranslations[currentLanguage];

  return (
    <div className="space-y-6">
      {/* الإحصائيات العامة */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{result.totalRecords}</div>
          <div className="text-sm text-gray-600 dark:text-gray-300">{t.analysis.totalRecords}</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">{result.statistics.totalFields}</div>
          <div className="text-sm text-gray-600 dark:text-gray-300">{t.analysis.totalFields}</div>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{result.statistics.idFields}</div>
          <div className="text-sm text-gray-600 dark:text-gray-300">{t.analysis.idFields}</div>
        </div>
        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{result.statistics.enumFields}</div>
          <div className="text-sm text-gray-600 dark:text-gray-300">{t.analysis.enumFields}</div>
        </div>
      </div>

      {/* الحقول المكتشفة */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
          {t.analysis.fields}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {result.fields.map((field, index) => (
            <FieldCard key={index} field={field} currentLanguage={currentLanguage} />
          ))}
        </div>
      </div>

      {/* العلاقات */}
      {result.structure.relationships.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
            {t.analysis.relationships}
          </h4>
          <div className="space-y-2">
            {result.structure.relationships.map((rel, index) => (
              <div key={index} className="flex justify-between items-center bg-white dark:bg-gray-800 rounded-lg p-3">
                <span className="text-gray-700 dark:text-gray-300">
                  {rel.from} → {rel.to}
                </span>
                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded text-sm">
                  {rel.type} ({(rel.strength * 100).toFixed(1)}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* الاقتراحات */}
      {result.suggestions.primaryKey && (
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
            {t.analysis.suggestions}
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center bg-white dark:bg-gray-800 rounded-lg p-3">
              <span className="text-gray-700 dark:text-gray-300">Primary Key:</span>
              <span className="font-mono text-blue-600 dark:text-blue-400">{result.suggestions.primaryKey}</span>
            </div>
            {result.suggestions.indexes.length > 0 && (
              <div className="flex justify-between items-center bg-white dark:bg-gray-800 rounded-lg p-3">
                <span className="text-gray-700 dark:text-gray-300">Indexes:</span>
                <span className="font-mono text-green-600 dark:text-green-400">
                  {result.suggestions.indexes.join(', ')}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FieldCard({ field, currentLanguage }: { field: DataField; currentLanguage: 'ar' | 'en' }) {
  const getTypeColor = () => {
    const colors: Record<string, string> = {
      'String': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'Number': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'Boolean': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'Date': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      'Object': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'Array': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    };
    return colors[field.type] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
      <div className="flex justify-between items-start mb-2">
        <h5 className="font-semibold text-gray-900 dark:text-white truncate">{field.name}</h5>
        <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor()}`}>
          {field.type}
        </span>
      </div>
      
      <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
        <div className="flex justify-between">
          <span>{currentLanguage === 'ar' ? 'فريد:' : 'Unique:'}</span>
          <span>{field.uniqueCount}</span>
        </div>
        {field.nullable && (
          <div className="text-orange-600 dark:text-orange-400">
            {currentLanguage === 'ar' ? 'قابل للفراغ' : 'Nullable'}
          </div>
        )}
        {field.isId && (
          <div className="text-blue-600 dark:text-blue-400">
            {currentLanguage === 'ar' ? 'معرف' : 'ID'}
          </div>
        )}
        {field.isEnum && (
          <div className="text-purple-600 dark:text-purple-400">
            {currentLanguage === 'ar' ? 'تعداد' : 'Enum'}
          </div>
        )}
      </div>
    </div>
  );
}

function CodeOutput({ analysis, currentLanguage }: { analysis: AnalysisResult; currentLanguage: 'ar' | 'en' }) {
  const [selectedLanguage, setSelectedLanguage] = useState('typescript');
  const [copiedLanguage, setCopiedLanguage] = useState<string | null>(null);
  const t = dataAnalyticsTranslations[currentLanguage];

  const languages = [
    { key: 'typescript', name: 'TypeScript', icon: 'TS' },
    { key: 'python', name: 'Python', icon: 'PY' },
    { key: 'sql', name: 'SQL', icon: 'SQL' },
    { key: 'go', name: 'Go', icon: 'GO' },
    { key: 'java', name: 'Java', icon: 'JV' },
    { key: 'csharp', name: 'C#', icon: 'CS' },
    { key: 'php', name: 'PHP', icon: 'PH' },
    { key: 'rust', name: 'Rust', icon: 'RS' },
    { key: 'swift', name: 'Swift', icon: 'SW' },
    { key: 'kotlin', name: 'Kotlin', icon: 'KT' },
    { key: 'zod', name: 'Zod', icon: 'ZOD' },
    { key: 'pydantic', name: 'Pydantic', icon: 'PD' },
    { key: 'django', name: 'Django', icon: 'DJ' },
    { key: 'fastapi', name: 'FastAPI', icon: 'FA' },
    { key: 'sqlalchemy', name: 'SQLAlchemy', icon: 'SA' },
    { key: 'mongoose', name: 'Mongoose', icon: 'MG' },
    { key: 'prisma', name: 'Prisma', icon: 'PR' },
    { key: 'sequelize', name: 'Sequelize', icon: 'SQ' },
    { key: 'typeorm', name: 'TypeORM', icon: 'TO' },
    { key: 'json-schema', name: 'JSON Schema', icon: 'JS' },
    { key: 'openapi', name: 'OpenAPI', icon: 'OA' },
    { key: 'protobuf', name: 'Protobuf', icon: 'PB' },
    { key: 'graphql', name: 'GraphQL', icon: 'GQ' }
  ];

  const generatedCode = useMemo(() => {
    return generateCodeOptimized(analysis, selectedLanguage);
  }, [analysis, selectedLanguage]);

  const handleCopy = useCallback(async (language: string) => {
    const code = generateCodeOptimized(analysis, language);
    await navigator.clipboard.writeText(code);
    setCopiedLanguage(language);
    setTimeout(() => setCopiedLanguage(null), 2000);
  }, [analysis]);

  const handleDownload = useCallback((language: string) => {
    const code = generateCodeOptimized(analysis, language);
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    // تحديد امتداد الملف حسب اللغة
    const getFileExtension = (lang: string) => {
      const extensions: Record<string, string> = {
        'typescript': 'ts',
        'python': 'py',
        'sql': 'sql',
        'go': 'go',
        'java': 'java',
        'csharp': 'cs',
        'php': 'php',
        'rust': 'rs',
        'swift': 'swift',
        'kotlin': 'kt',
        'zod': 'ts',
        'pydantic': 'py',
        'django': 'py',
        'fastapi': 'py',
        'sqlalchemy': 'py',
        'mongoose': 'js',
        'prisma': 'prisma',
        'sequelize': 'js',
        'typeorm': 'ts',
        'json-schema': 'json',
        'openapi': 'yaml',
        'protobuf': 'proto',
        'graphql': 'graphql'
      };
      return extensions[lang] || 'txt';
    };
    
    a.download = `schema.${getFileExtension(language)}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [analysis]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
      className="w-full max-w-6xl mx-auto"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          {t.outputTitle}
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          {t.outputSubtitle}
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        {/* اختيار اللغة */}
        <div className="flex flex-wrap gap-2 mb-6">
          {languages.map((lang) => (
            <button
              key={lang.key}
              onClick={() => setSelectedLanguage(lang.key)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedLanguage === lang.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {lang.icon}
            </button>
          ))}
        </div>

        {/* الكود المُولد */}
        <div className="bg-gray-900 rounded-lg p-4 relative">
          <div className="flex items-center justify-between mb-4">
            <span className="text-white font-medium capitalize">{selectedLanguage}</span>
            <div className="flex gap-2">
              <button
                onClick={() => handleCopy(selectedLanguage)}
                className="text-gray-400 hover:text-white transition-colors p-1"
                title={copiedLanguage === selectedLanguage ? t.actions.copied : t.actions.copy}
              >
                <FaCopy className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDownload(selectedLanguage)}
                className="text-gray-400 hover:text-white transition-colors p-1"
                title={t.actions.download}
              >
                <FaDownload className="w-4 h-4" />
              </button>
            </div>
          </div>
          <pre className="text-green-400 text-sm overflow-x-auto">
            <code>{generatedCode}</code>
          </pre>
        </div>
      </div>
    </motion.div>
  );
}

function FeaturesSection({ currentLanguage }: { currentLanguage: 'ar' | 'en' }) {
  const t = dataAnalyticsTranslations[currentLanguage];

  return (
    <section className="py-16 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            {t.features.title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.features.items.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg text-center hover:shadow-xl transition-shadow"
            >
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <FeatureIcon icon={feature.icon} className="text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// مكون للرسوم البيانية
interface ChartDataset {
  data: number[];
  label?: string;
  backgroundColor?: string | string[];
  borderColor?: string | string[];
  borderWidth?: number;
  fill?: boolean;
  tension?: number;
  [key: string]: unknown;
}

interface ChartConfig {
  type: string;
  data: {
    labels: string[];
    datasets: ChartDataset[];
  };
  options?: {
    plugins?: {
      title?: { text?: string };
      legend?: { labels?: { color?: string; font?: { size?: number } } };
    };
  };
}

function ChartComponent({ chart, currentLanguage }: { chart: ChartConfig; currentLanguage: 'ar' | 'en' }) {
  const renderChart = () => {
    const chartData = {
      labels: chart.data.labels,
      datasets: chart.data.datasets.map((dataset: ChartDataset) => ({
        ...dataset,
        backgroundColor: dataset.backgroundColor || '#8B5CF6',
        borderColor: dataset.borderColor || '#7C3AED',
      }))
    };

    const options = {
      ...chart.options,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        ...chart.options?.plugins,
        legend: {
          ...chart.options?.plugins?.legend,
          labels: {
            color: currentLanguage === 'ar' ? '#374151' : '#374151',
            font: { size: 12 }
          }
        }
      }
    };

    switch (chart.type) {
      case 'bar':
        return <Bar data={chartData} options={options} />;
      case 'line':
        return <Line data={chartData} options={options} />;
      case 'doughnut':
      case 'pie':
        return <Doughnut data={chartData} options={options} />;
      case 'scatter':
        return <Scatter data={chartData} options={options} />;
      default:
        return <Bar data={chartData} options={options} />;
    }
  };

  return (
    <div className="h-64">
      {renderChart()}
    </div>
  );
}

function DataVisualization({ data, currentLanguage }: { data: string; currentLanguage: 'ar' | 'en' }) {
  const [visualizationResult, setVisualizationResult] = useState<{ charts: ChartConfig[]; insights: string[]; recommendations: string[] } | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const generateVisualization = useCallback(async () => {
    if (!data) return;
    
    setIsGenerating(true);
    try {
      const result = analyzeDataForVisualization(data);
      setVisualizationResult(result);
    } catch (error) {
      console.error('Error generating visualization:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [data]);

  React.useEffect(() => {
    if (data) {
      generateVisualization();
    }
  }, [data, generateVisualization]);

  if (!data) return null;

  return (
    <section className="py-16 bg-gray-50 dark:bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            {currentLanguage === 'ar' ? 'التصوير البصري للبيانات' : 'Data Visualization'}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            {currentLanguage === 'ar' 
              ? 'اكتشف الأنماط والعلاقات في بياناتك من خلال الرسوم البيانية التفاعلية'
              : 'Discover patterns and relationships in your data through interactive charts'
            }
          </p>
        </motion.div>

        {isGenerating ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-white transition ease-in-out duration-150 cursor-not-allowed">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {currentLanguage === 'ar' ? 'جاري إنشاء الرسوم البيانية...' : 'Generating charts...'}
            </div>
          </div>
        ) : visualizationResult ? (
          <div className="space-y-8">
            {/* Quick Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white dark:bg-gray-700 rounded-2xl p-6 shadow-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                {currentLanguage === 'ar' ? 'ملخص سريع' : 'Quick Summary'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {visualizationResult.charts.map((chart: ChartConfig, index: number) => (
                  <div key={index} className="text-center p-4 bg-gray-50 dark:bg-gray-600 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {chart.data.datasets[0].data.length}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      {currentLanguage === 'ar' ? 'نقطة بيانات' : 'Data Points'}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {visualizationResult.charts.map((chart: ChartConfig, index: number) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                  className="bg-white dark:bg-gray-700 rounded-2xl p-6 shadow-lg"
                >
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    {chart.options?.plugins?.title?.text || `Chart ${index + 1}`}
                  </h3>
                                     <div className="h-64 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900 dark:to-indigo-900 rounded-lg p-4">
                     <ChartComponent chart={chart} currentLanguage={currentLanguage} />
                   </div>
                </motion.div>
              ))}
            </div>

            {/* Insights */}
            {visualizationResult.insights.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white dark:bg-gray-700 rounded-2xl p-6 shadow-lg"
              >
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {currentLanguage === 'ar' ? 'الرؤى المكتشفة' : 'Discovered Insights'}
                </h3>
                <div className="space-y-2">
                  {visualizationResult.insights.map((insight: string, index: number) => (
                    <div key={index} className="flex items-start space-x-3 rtl:space-x-reverse">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-gray-700 dark:text-gray-300">{insight}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Recommendations */}
            {visualizationResult.recommendations.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900 dark:to-indigo-900 rounded-2xl p-6 shadow-lg"
              >
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {currentLanguage === 'ar' ? 'التوصيات' : 'Recommendations'}
                </h3>
                <div className="space-y-2">
                  {visualizationResult.recommendations.map((recommendation: string, index: number) => (
                    <div key={index} className="flex items-start space-x-3 rtl:space-x-reverse">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-gray-700 dark:text-gray-300">{recommendation}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function ExamplesSection({ currentLanguage, onExampleSelect }: { currentLanguage: 'ar' | 'en'; onExampleSelect: (data: string) => void }) {
  const t = dataAnalyticsTranslations[currentLanguage];

  return (
    <section className="py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            {t.examples.title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {t.examples.items.map((example, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 + index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
              onClick={() => onExampleSelect(example.data)}
            >
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {example.name}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {example.description}
              </p>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                {currentLanguage === 'ar' ? 'استخدم هذا المثال' : 'Use This Example'}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =====================
// الصفحة الرئيسية
// =====================
export default function DataAnalyticsPage() {
  const { t, currentLanguage, toggleLanguage } = useTranslation();
  const [data, setData] = useState('');
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [showNav, setShowNav] = useState(true);
  const [showDashboard, setShowDashboard] = useState(false);

  // منطق إخفاء/إظهار القائمة عند التمرير
  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY;
    if (scrollY > 100) {
      setShowNav(false);
    } else {
      setShowNav(true);
    }
  }, []);

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleAnalysisComplete = useCallback((result: AnalysisResult) => {
    setAnalysisResult(result);
  }, []);

  const handleClear = useCallback(() => {
    setData('');
    setAnalysisResult(null);
  }, []);

  const dataT = t.dataAnalytics;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: showNav ? 0 : -100 }}
        transition={{ duration: 0.3 }}
        className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200 dark:border-gray-700"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="flex items-center space-x-3 text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              <AiFillHome className="w-6 h-6" />
              <span className="font-semibold">
                {currentLanguage === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
              </span>
            </Link>

            <div className="flex items-center space-x-4">
              <button
                onClick={toggleLanguage}
                className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all duration-300 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <FaGlobe className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-purple-900" />
        
        {/* Floating Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-20 w-32 h-32 bg-blue-200/30 rounded-full blur-2xl animate-pulse" />
          <div className="absolute bottom-20 right-20 w-24 h-24 bg-purple-200/30 rounded-full blur-2xl animate-pulse" />
          <div className="absolute top-1/2 left-1/3 w-20 h-20 bg-indigo-200/20 rounded-full blur-2xl animate-pulse" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 dark:text-white mb-6">
              {dataT.title}
            </h1>
            <p className="text-xl sm:text-2xl lg:text-3xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto">
              {dataT.subtitle}
            </p>
          </motion.div>

          {/* Data Upload Section */}
          <DataUpload onDataChange={setData} currentLanguage={currentLanguage as 'ar' | 'en'} onClear={handleClear} />
        </div>
      </section>

      {/* Data Analysis Section */}
      {data && (
        <section className="py-16">
          <DataAnalysis 
            data={data} 
            currentLanguage={currentLanguage as 'ar' | 'en'} 
            onAnalysisComplete={handleAnalysisComplete} 
          />
        </section>
      )}

                {/* Dashboard Controls */}
          {data && (
            <section className="py-8 bg-gray-50 dark:bg-gray-800">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-center space-x-4 rtl:space-x-reverse">
                  <button
                    onClick={() => setShowDashboard(!showDashboard)}
                    className="flex items-center space-x-2 rtl:space-x-reverse px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    <FaTachometerAlt className="w-5 h-5" />
                    <span className="font-semibold">
                      {showDashboard 
                        ? t.dataAnalytics.dashboard.hide
                        : t.dataAnalytics.dashboard.show
                      }
                    </span>
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Advanced Dashboard */}
          {showDashboard && data && (
            <AdvancedDashboard 
              data={data} 
              currentLanguage={currentLanguage as 'ar' | 'en'}
              onExport={(dashboardData) => {
                const blob = new Blob([JSON.stringify(dashboardData, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'dashboard-config.json';
                a.click();
                URL.revokeObjectURL(url);
              }}
              onShare={() => {
                const shareData = {
                  title: t.dataAnalytics.dashboard.title,
                  text: t.dataAnalytics.dashboard.share,
                  url: window.location.href
                };
                if (navigator.share) {
                  navigator.share(shareData);
                } else {
                  navigator.clipboard.writeText(window.location.href);
                  alert(currentLanguage === 'ar' ? 'تم نسخ الرابط!' : 'Link copied!');
                }
              }}
            />
          )}

          {/* Data Visualization Section */}
          {data && !showDashboard && (
            <DataVisualization 
              data={data} 
              currentLanguage={currentLanguage as 'ar' | 'en'} 
            />
          )}

      {/* Code Output Section */}
      {analysisResult && (
        <section className="py-16">
          <CodeOutput analysis={analysisResult} currentLanguage={currentLanguage as 'ar' | 'en'} />
        </section>
      )}

      {/* Features Section */}
      <FeaturesSection currentLanguage={currentLanguage as 'ar' | 'en'} />

      {/* Performance Optimization Section */}
      <section className="py-16 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900 dark:to-indigo-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              {currentLanguage === 'ar' ? 'تحسينات الأداء' : 'Performance Optimizations'}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              {currentLanguage === 'ar' 
                ? 'تم تحسين التطبيق ليعمل بسرعة عالية حتى مع البيانات الكبيرة'
                : 'The application has been optimized to work at high speed even with large datasets'
              }
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* تحسين العينات */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg"
            >
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {currentLanguage === 'ar' ? 'تحليل العينات' : 'Sample Analysis'}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {currentLanguage === 'ar'
                  ? 'تحليل سريع باستخدام عينات من البيانات بدلاً من المجموعة الكاملة'
                  : 'Fast analysis using data samples instead of the full dataset'
                }
              </p>
            </motion.div>

            {/* المعالجة المتوازية */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg"
            >
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">🔄</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {currentLanguage === 'ar' ? 'معالجة متوازية' : 'Parallel Processing'}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {currentLanguage === 'ar'
                  ? 'تحليل الحقول بشكل متوازي لتحسين السرعة'
                  : 'Parallel field analysis for improved speed'
                }
              </p>
            </motion.div>

            {/* شريط التقدم */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg"
            >
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {currentLanguage === 'ar' ? 'شريط التقدم' : 'Progress Tracking'}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {currentLanguage === 'ar'
                  ? 'متابعة التقدم في الوقت الفعلي مع تقديرات الوقت'
                  : 'Real-time progress tracking with time estimates'
                }
              </p>
            </motion.div>

            {/* تحسين الذاكرة */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg"
            >
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">💾</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {currentLanguage === 'ar' ? 'تحسين الذاكرة' : 'Memory Optimization'}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {currentLanguage === 'ar'
                  ? 'استخدام ذكي للذاكرة مع تنظيف تلقائي'
                  : 'Smart memory usage with automatic cleanup'
                }
              </p>
            </motion.div>

            {/* تحليل ذكي */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg"
            >
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">🧠</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {currentLanguage === 'ar' ? 'تحليل ذكي' : 'Smart Analysis'}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {currentLanguage === 'ar'
                  ? 'كشف ذكي للأنواع مع تقليل العمليات غير الضرورية'
                  : 'Intelligent type detection with reduced unnecessary operations'
                }
              </p>
            </motion.div>

            {/* واجهة محسنة */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg"
            >
              <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">🎨</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {currentLanguage === 'ar' ? 'واجهة محسنة' : 'Enhanced UI'}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {currentLanguage === 'ar'
                  ? 'واجهة مستخدم محسنة مع رسوم متحركة سلسة'
                  : 'Enhanced user interface with smooth animations'
                }
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Examples Section */}
      <ExamplesSection 
        currentLanguage={currentLanguage as 'ar' | 'en'} 
        onExampleSelect={setData}
      />

      {/* Footer */}
      <footer className="text-center py-8 text-gray-600 dark:text-gray-400">
        <p>
          {currentLanguage === 'ar' 
            ? 'محلل البيانات الذكي المتقدم - أداة مجانية لتحليل البيانات وتوليد الأكواد'
            : 'Advanced Smart Data Analyzer - Free tool for data analysis and code generation'
          }
        </p>
      </footer>
    </div>
  );
} 